import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  Paper,
  Link,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  //useMediaQuery,
  ThemeProvider,
  CssBaseline,
  Alert,
  FormControl,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Login } from '../../logic/aplication/login';
import { LoginService } from '../../logic/domain/services/loginService';
import { AuthImplementation } from '../../logic/infraestructure/auth/authImplementation';
import glymTheme from '../../theme';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import logo from '../assets/logo.png';

interface LoginPageProps {
  onLogin: (response: { token: string }) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const authImplementation = new AuthImplementation();
  const loginService = new LoginService(authImplementation);
  const login = new Login(loginService);

  const [dni, setDni] = useState('DNI');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe //, setRememberMe
          ] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await login.execute(dni, username, password, rememberMe);
      onLogin({ token: response });
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error instanceof Error && error.message 
        ? error.message 
        : "Usuario o contraseña inválidos";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  //const isSmallScreen = useMediaQuery(glymTheme.breakpoints.down('sm'));

  return (
    <ThemeProvider theme={glymTheme}>
      <Grid
        container
        component="main"
        sx={{ height: "100vh" }}
      >
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={7}
          md={8}
          sx={{
            backgroundImage: process.env.REACT_APP_BACKGROUND_IMAGE,
            backgroundRepeat: "no-repeat",
            backgroundColor: "#2FA3A2",
            backgroundSize: "contain",
            backgroundPosition: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <Box
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.9)",
              p: { xs: 1.5, sm: 2 },
              width: "100%",
              textAlign: "center",
              borderTop: "1px solid rgba(0, 0, 0, 0.1)",
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <Typography variant="caption" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
              Este sistema cumple con los requisitos propuestos en la ley
              23.798 para codificación de datos de pacientes que soliciten HIV
              y otros análisis reservados.
              <br />
              Este sistema WEB no informa resultados de determinaciones
              privadas (HIV, Hepatitis, etc).
              <br />
              El solo uso de esta aplicación implica el consentimiento para
              recibir resultados en formato electrónico.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={5} md={4} component={Paper} elevation={6} square
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "auto"
          }}
        >
          <AppBar position="static" sx={{ background: "#00A3A8", boxShadow: "none" }}>
            <Toolbar sx={{ minHeight: "50px", justifyContent: "center" }}>
              <img src={logo} alt="Logo" style={{ height: 35 }} />
            </Toolbar>
          </AppBar>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 4,
              py: 4,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 400,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
                Iniciar sesión
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 2, width: "100%" }}>{error}</Alert>}
              <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="tipoDoc-label">Tipo de Documento</InputLabel>
                  <Select
                    labelId="tipoDoc-label"
                    id="tipoDoc"
                    value={dni}
                    label="Tipo de Documento"
                    onChange={(e) => setDni(e.target.value)}
                    required
                  >
                    <MenuItem value="DNI">DNI</MenuItem>
                    <MenuItem value="CUIL">CUIL</MenuItem>
                    <MenuItem value="CI">CI</MenuItem>
                    <MenuItem value="DNIN">DNIN</MenuItem>
                    <MenuItem value="*">*</MenuItem>
                    <MenuItem value="H.C">H.C</MenuItem>
                    <MenuItem value="LOTE">LOTE</MenuItem>
                    <MenuItem value="LC">LC</MenuItem>
                    <MenuItem value="E">E</MenuItem>
                    <MenuItem value="NIN">NIN</MenuItem>
                    <MenuItem value="PPE">PPE</MenuItem>
                    <MenuItem value="PAS">PAS</MenuItem>
                    <MenuItem value="SC">SC</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Número de Documento"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
                <TextField
                  label="Contraseña"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="show password"
                          onMouseDown={() => setShowPassword(true)}
                          onMouseUp={() => setShowPassword(false)}
                          onMouseLeave={() => setShowPassword(false)}
                          onTouchStart={() => setShowPassword(true)}
                          onTouchEnd={() => setShowPassword(false)}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 2, color: "white" }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Iniciar Sesión'}
                </Button>
              </Box>
              <Grid container sx={{ mt: 2 }}>
                <Grid item xs>
                  <Link
                    href="#/recover-password"
                    variant="body2"
                  >
                    ¿Has olvidado tu contraseña?
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.9)",
              p: { xs: 1.5, sm: 2 },
              width: "100%",
              textAlign: "center",
              borderTop: "1px solid rgba(0, 0, 0, 0.1)",
              flexShrink: 0,
              display: { xs: 'block', sm: 'none' },
            }}
          >
            <Typography variant="caption" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
              Este sistema cumple con los requisitos propuestos en la ley
              23.798 para codificación de datos de pacientes que soliciten HIV
              y otros análisis reservados.
              <br />
              Este sistema WEB no informa resultados de determinaciones
              privadas (HIV, Hepatitis, etc).
              <br />
              El solo uso de esta aplicación implica el consentimiento para
              recibir resultados en formato electrónico.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default LoginPage;
